import { useState } from 'react';
import { Trans, useLingui } from '@lingui/react/macro';
import {
  Download,
  ExternalLink,
  File,
  FileArchive,
  FileAudio,
  FileCode,
  FileSpreadsheet,
  FileText,
  FileVideo,
  FolderOpen,
  Link as LinkIcon,
  Loader2,
  Maximize2,
  MoreVertical,
} from 'lucide-react';
import type { LucideIcon } from 'lucide-react';
import { cn } from '@src/lib/utils';
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from '@src/components/ui/dropdown-menu';
import { AssetEditor, editorForPath } from '@src/navigation/asset-doc-types';
import { isImagePath } from '@sdk';
import { useValueKind, valueKindMeta } from '@src/components/value-viewer/useValueKind';
import { MediaLightbox, isVideoPath, videoSource } from '@src/components/ui/media-lightbox';

function extOf(name: string): string {
  const dot = name.lastIndexOf('.');
  return dot >= 0 ? name.slice(dot + 1).toLowerCase() : '';
}

interface FileMeta {
  Icon: LucideIcon;
  bg: string;
  label: string;
}

function fileMeta(name: string): FileMeta {
  const ext = extOf(name);
  const upper = ext ? ext.toUpperCase() : 'FILE';
  if (ext === 'pdf') return { Icon: FileText, bg: 'bg-red-500', label: 'PDF' };
  if (['doc', 'docx', 'rtf', 'odt'].includes(ext)) return { Icon: FileText, bg: 'bg-blue-500', label: upper };
  if (['xls', 'xlsx', 'csv', 'ods', 'tsv'].includes(ext))
    return { Icon: FileSpreadsheet, bg: 'bg-emerald-600', label: upper };
  if (['ppt', 'pptx', 'odp', 'key'].includes(ext)) return { Icon: FileText, bg: 'bg-orange-500', label: upper };
  if (['zip', 'tar', 'gz', '7z', 'rar', 'bz2', 'xz'].includes(ext))
    return { Icon: FileArchive, bg: 'bg-yellow-600', label: upper };
  if (['mp3', 'wav', 'ogg', 'flac', 'm4a', 'aac'].includes(ext))
    return { Icon: FileAudio, bg: 'bg-indigo-500', label: upper };
  if (['mp4', 'mov', 'avi', 'mkv', 'webm'].includes(ext)) return { Icon: FileVideo, bg: 'bg-pink-500', label: upper };
  if (
    [
      'js',
      'jsx',
      'ts',
      'tsx',
      'json',
      'html',
      'htm',
      'css',
      'scss',
      'sass',
      'py',
      'java',
      'c',
      'h',
      'cpp',
      'hpp',
      'rb',
      'go',
      'rs',
      'sh',
      'bash',
      'zsh',
      'yaml',
      'yml',
      'toml',
      'xml',
      'sql',
      'php',
      'kt',
      'swift',
    ].includes(ext)
  ) {
    return { Icon: FileCode, bg: 'bg-purple-500', label: upper };
  }
  if (['md', 'mdx', 'txt', 'log', 'rst'].includes(ext)) return { Icon: FileText, bg: 'bg-slate-500', label: upper };
  return { Icon: File, bg: 'bg-slate-500', label: upper };
}

function absoluteUrl(url: string): string {
  if (/^https?:\/\//i.test(url)) return url;
  return window.location.origin + (url.startsWith('/') ? url : `/${url}`);
}

/** Body-bundle lifecycle as the chip sees it:
 *  - Uploading    : sender is still staging the body — bytes not on the hub yet.
 *  - Ready        : body is on the hub but not on this machine — click to pull.
 *  - Downloaded   : bytes are local — open/save normally (also: text-only and
 *                   purely-local conversations, which never round-trip a body).
 *  - Unavailable  : there is no body to fetch (``body_status='na'`` and the
 *                   bytes are not local) — a dangling pointer. Inert: we render
 *                   a muted row and NEVER a live URL, so nothing 404s. */
export enum AttachmentChipState {
  Uploading = 'uploading',
  Ready = 'ready',
  Downloaded = 'downloaded',
  Unavailable = 'unavailable',
}

interface AttachmentChipProps {
  url: string;
  filename: string;
  /** Defaults to Downloaded — the file is local and openable. */
  state?: AttachmentChipState;
  /** Invoked when a READY chip is clicked — triggers the body-bundle pull. */
  onDownload?: () => void;
  /** True while that body-bundle pull is in flight. */
  downloading?: boolean;
  /** Downloaded files only: open the file in the editor (the standard
   *  `DockPointer.forFile` path, same as the interactive terminal's file
   *  tree). When provided, it becomes the *primary* click on a downloaded
   *  non-media file card; raw download stays available in the overlay menu. */
  onOpenInEditor?: () => void;
  /** Downloaded files only: reveal the file in the OS file manager (Finder /
   *  Explorer). Reuses the interactive terminal's reveal-in-folder helper.
   *  When provided, an "open external folder" icon shows in the overlay. */
  onRevealInFolder?: () => void;
  /** The file's local path, when it is on this machine. A value file (`*.value.json`) is read
   *  from it so the chip names the value's kind instead of "JSON". */
  localPath?: string | null;
}

/** What "open" does for this file: browse an archive, show a value, or edit text. */
function openLabel(editor: AssetEditor) {
  if (editor === AssetEditor.ARCHIVE) return <Trans>Preview contents</Trans>;
  if (editor === AssetEditor.VALUE) return <Trans>Open</Trans>;
  return <Trans>Open in editor</Trans>;
}

export function AttachmentChip({
  url,
  filename,
  state = AttachmentChipState.Downloaded,
  onDownload,
  downloading = false,
  onOpenInEditor,
  onRevealInFolder,
  localPath,
}: AttachmentChipProps) {
  const { t } = useLingui();
  const valueKind = useValueKind(state === AttachmentChipState.Downloaded ? localPath : null);
  const [imgFailed, setImgFailed] = useState(false);
  const [videoFailed, setVideoFailed] = useState(false);
  const [lightbox, setLightbox] = useState(false);
  // UPLOADING / READY / UNAVAILABLE: the bytes aren't on this machine, so there
  // is no live URL to link or inline-render. Render a status row instead —
  // greyed + inert for UPLOADING, dashed + clickable (→ download) for READY,
  // muted + inert for UNAVAILABLE (a dangling pointer with no body to fetch).
  // Placed after every hook so hook order stays stable across renders.
  if (state !== AttachmentChipState.Downloaded) {
    const { Icon, bg, label } = fileMeta(filename);
    const isUploading = state === AttachmentChipState.Uploading;
    const isUnavailable = state === AttachmentChipState.Unavailable;
    const inert = isUploading || isUnavailable;
    const clickable = state === AttachmentChipState.Ready && !downloading && !!onDownload;
    const { sub, title } = isUploading
      ? { sub: t`Uploading…`, title: t`File not uploaded yet` }
      : isUnavailable
        ? { sub: t`Unavailable`, title: t`Attachment unavailable — no body was uploaded` }
        : downloading
          ? { sub: t`Downloading…`, title: t`Downloading…` }
          : { sub: `${label} · Download`, title: t`Click to download` };
    return (
      <div className="max-w-[360px]">
        <button
          type="button"
          disabled={!clickable}
          onClick={clickable ? () => onDownload?.() : undefined}
          title={title}
          className={cn(
            'flex w-full items-center gap-3 rounded-lg border px-3 py-2.5 text-start transition-colors',
            inert
              ? 'cursor-not-allowed border-border bg-background opacity-50'
              : clickable
                ? 'cursor-pointer border-dashed border-primary/60 bg-background hover:bg-muted/40'
                : 'cursor-default border-dashed border-primary/60 bg-background',
          )}
        >
          <div
            className={cn(
              'flex h-10 w-10 shrink-0 items-center justify-center rounded text-white',
              inert ? 'bg-slate-400' : bg,
            )}
          >
            {downloading ? <Loader2 className="h-5 w-5 animate-spin" /> : <Icon className="h-5 w-5" />}
          </div>
          <div className="flex min-w-0 flex-col">
            <span className="truncate text-sm font-medium text-foreground">{filename}</span>
            <span className="text-[11px] uppercase tracking-wide text-muted-foreground">{sub}</span>
          </div>
        </button>
      </div>
    );
  }

  const handleCopyLink = async () => {
    try {
      await navigator.clipboard.writeText(absoluteUrl(url));
    } catch {
      // ignore — clipboard may be blocked outside secure context
    }
  };

  const overlay = (
    <div className="absolute right-1 top-1 z-10">
      <div className="flex items-center gap-0.5 rounded-md border border-border bg-background/95 p-0.5 shadow-sm backdrop-blur-sm">
        <a
          href={url}
          download={filename}
          title={t`Download`}
          onClick={(e) => e.stopPropagation()}
          className="flex h-6 w-6 items-center justify-center rounded text-muted-foreground transition-colors hover:bg-muted hover:text-foreground"
        >
          <Download className="h-3.5 w-3.5" />
        </a>
        {onRevealInFolder && (
          <button
            type="button"
            title={t`Reveal in folder`}
            onClick={(e) => {
              e.preventDefault();
              e.stopPropagation();
              onRevealInFolder();
            }}
            className="flex h-6 w-6 items-center justify-center rounded text-muted-foreground transition-colors hover:bg-muted hover:text-foreground"
          >
            <FolderOpen className="h-3.5 w-3.5" />
          </button>
        )}
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <button
              type="button"
              title={t`More actions`}
              onClick={(e) => e.stopPropagation()}
              className="flex h-6 w-6 items-center justify-center rounded text-muted-foreground transition-colors hover:bg-muted hover:text-foreground"
            >
              <MoreVertical className="h-3.5 w-3.5" />
            </button>
          </DropdownMenuTrigger>
          {/* Portaled, but React events still bubble to the chip's host — stop them here. */}
          <DropdownMenuContent align="end" className="min-w-[160px]" onClick={(e) => e.stopPropagation()}>
            {onOpenInEditor && (
              <DropdownMenuItem className="text-xs" onSelect={onOpenInEditor}>
                <FileText className="text-muted-foreground" />
                {openLabel(editorForPath(filename))}
              </DropdownMenuItem>
            )}
            <DropdownMenuItem className="text-xs" asChild>
              <a href={url} target="_blank" rel="noreferrer">
                <ExternalLink className="text-muted-foreground" />
                <Trans>Open in new tab</Trans>
              </a>
            </DropdownMenuItem>
            {onRevealInFolder && (
              <DropdownMenuItem className="text-xs" onSelect={onRevealInFolder}>
                <FolderOpen className="text-muted-foreground" />
                <Trans>Reveal in folder</Trans>
              </DropdownMenuItem>
            )}
            <DropdownMenuItem className="text-xs" onSelect={() => void handleCopyLink()}>
              <LinkIcon className="text-muted-foreground" />
              <Trans>Copy link</Trans>
            </DropdownMenuItem>
            <DropdownMenuItem className="text-xs" asChild>
              <a href={url} download={filename}>
                <Download className="text-muted-foreground" />
                <Trans>Download</Trans>
              </a>
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
      </div>
    </div>
  );

  const lightboxNode = lightbox && <MediaLightbox url={url} name={filename} onClose={() => setLightbox(false)} />;

  if (isImagePath(filename) && !imgFailed) {
    return (
      <div className="group relative inline-block">
        {/* Primary click previews the image in-app (lightbox), not a browser
            tab. Download / open-in-new-tab / reveal stay in the overlay menu. */}
        <button
          type="button"
          onClick={() => setLightbox(true)}
          className="block max-w-[360px] cursor-zoom-in overflow-hidden rounded-lg border border-border bg-muted/40"
          title={filename}
        >
          <img
            src={url}
            alt={filename}
            onError={() => setImgFailed(true)}
            className="block max-h-[280px] max-w-full object-contain"
          />
        </button>
        {overlay}
        {lightboxNode}
      </div>
    );
  }

  if (isVideoPath(filename) && !videoFailed) {
    return (
      <div className="group relative inline-block">
        <div className="block max-w-[360px] overflow-hidden rounded-lg border border-border bg-black" title={filename}>
          <video
            controls
            preload="metadata"
            playsInline
            onError={() => setVideoFailed(true)}
            className="block max-h-[280px] max-w-full bg-black"
          >
            {videoSource(url, filename)}
          </video>
        </div>
        {/* Native controls own clicks on the player body, so expand-to-fullscreen
            is an explicit affordance rather than a click-anywhere target. */}
        <button
          type="button"
          title={t`Expand`}
          onClick={(e) => {
            e.preventDefault();
            e.stopPropagation();
            setLightbox(true);
          }}
          className="absolute left-1 top-1 z-10 flex h-6 w-6 items-center justify-center rounded-md border border-border bg-background/95 text-muted-foreground opacity-0 shadow-sm backdrop-blur-sm transition-opacity hover:bg-muted hover:text-foreground group-hover:opacity-100"
        >
          <Maximize2 className="h-3.5 w-3.5" />
        </button>
        {overlay}
        {lightboxNode}
      </div>
    );
  }

  // A value of a kind is shown as that kind, not as the JSON it is stored in.
  const { Icon, bg, label } = valueKind
    ? { ...valueKindMeta(valueKind), bg: 'bg-teal-600' }
    : fileMeta(filename);

  const cardInner = (
    <>
      <div className={cn('flex h-10 w-10 shrink-0 items-center justify-center rounded text-white', bg)}>
        <Icon className="h-5 w-5" />
      </div>
      <div className="flex min-w-0 flex-col pe-14">
        <span className="truncate text-sm font-medium text-foreground">{filename}</span>
        <span className="text-[11px] uppercase tracking-wide text-muted-foreground">{label}</span>
      </div>
    </>
  );
  const cardClass =
    'flex w-full items-center gap-3 rounded-lg border border-border bg-background px-3 py-2.5 text-start transition-colors hover:bg-muted/40';

  return (
    <div className="group relative max-w-[360px]">
      {/* Primary click opens the file in the editor (standard file dock
          pointer) when the host wires it; otherwise the card is the raw
          download link. Either way the overlay keeps Download + open-in-tab. */}
      {onOpenInEditor ? (
        <button type="button" onClick={onOpenInEditor} title={`Open ${filename}`} className={cardClass}>
          {cardInner}
        </button>
      ) : (
        <a href={url} download={filename} target="_blank" rel="noreferrer" title={filename} className={cardClass}>
          {cardInner}
        </a>
      )}
      {overlay}
    </div>
  );
}
